import { useEffect, useState } from 'react';
import { api } from '../api';
import type { Project, ProjectStatus } from '../api';
import { Link, navigate, useQuery } from '../router';
import { useSession } from '../session';
import { useData } from '../useData';
import { Shell } from '../components/Shell';
import { ProjectForm } from '../components/ProjectForm';
import { TagSelect } from '../components/TagSelect';
import { Empty, Hours, Loading, useStatusName } from '../components/bits';

const STATUSES: ProjectStatus[] = [
  'in_progress', 'planning', 'on_hold', 'placeholder', 'complete', 'archived',
];

/** Where a sign-in lands.
 *
 *  A client with one workspace sees their projects straight away, which is
 *  what Drupal's login redirect did and the only part of it worth keeping.
 *  Staff get a client filter instead, since they have thirty-three.
 *
 *  What is shown by default is the work in progress. Of 252 projects, 41 are
 *  live and 196 are finished or archived, so listing everything buries the
 *  handful anybody came to see. The filter says what it is doing, and nine of
 *  the fifty-three clients have nothing in progress at all, so the empty state
 *  has to offer them the way out rather than look broken. */
export function Home() {
  const { t, user } = useSession();
  const statusName = useStatusName();
  const params = useQuery();

  const [creating, setCreating] = useState(false);
  const [workspaceId, setWorkspaceId] = useState<number | undefined>(numberOr(params.get('workspaceId')));
  const [status, setStatus] = useState<ProjectStatus | 'all'>(
    (params.get('status') as ProjectStatus | 'all') || 'in_progress',
  );

  // The filters are the URL, so a view can be kept or sent.
  useEffect(() => {
    const q = new URLSearchParams();
    if (workspaceId) q.set('workspaceId', String(workspaceId));
    if (status !== 'in_progress') q.set('status', status);
    const next = q.toString() ? `/projects?${q}` : '/projects';
    if (next !== window.location.pathname + window.location.search) navigate(next, true);
  }, [workspaceId, status]);

  const workspaces = useData(() => api.workspaces(), []);
  const projects = useData(
    () => api.projects({ workspaceId, status: status === 'all' ? undefined : status }),
    [workspaceId, status],
  );

  const many = (workspaces.data?.length ?? 0) > 1;

  return (
    <Shell>
      <div className="page-head">
        <div className="grow">
          <p className="kicker">{user?.isStaff ? t.all : workspaces.data?.[0]?.name}</p>
          <h1>{t.projects}</h1>
        </div>
        {user?.isStaff && (
          <button className="primary" onClick={() => setCreating((v) => !v)}>
            {creating ? t.cancel : `${t.newProject} >`}
          </button>
        )}
      </div>

      {creating && (
        <ProjectForm
          workspaceId={workspaceId}
          onDone={() => { setCreating(false); projects.reload(); }}
          onCancel={() => setCreating(false)}
        />
      )}

      <div className="row" style={{ marginBottom: 'var(--sp-4)', alignItems: 'flex-end' }}>
        <div style={{ minWidth: 190 }}>
          <label htmlFor="status">{t.status}</label>
          <select
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value as ProjectStatus | 'all')}
          >
            {STATUSES.map((st) => (
              <option key={st} value={st}>{statusName(st)}</option>
            ))}
            <option value="all">{t.all}</option>
          </select>
        </div>
        {many && (
          <div style={{ minWidth: 220 }}>
            <label htmlFor="ws">{t.client}</label>
            <TagSelect
              id="ws"
              value={workspaceId}
              placeholder={t.all}
              options={(workspaces.data ?? []).map((w) => ({ value: w.id, label: w.name }))}
              onChange={setWorkspaceId}
            />
          </div>
        )}
      </div>

      {projects.loading && !projects.data ? <Loading /> : null}

      {projects.data?.length === 0 && (
        status === 'all' ? (
          <Empty>{t.noProjects}</Empty>
        ) : (
          <p className="empty">
            {t.nothingInStatus(statusName(status))}{' '}
            <button className="primary" onClick={() => setStatus('all')}>{t.showAll} &gt;</button>
          </p>
        )
      )}

      <div className="cards">
        {projects.data?.map((p) => <ProjectCard key={p.id} project={p} statusName={statusName} />)}
      </div>
    </Shell>
  );
}

function ProjectCard({ project, statusName }: { project: Project; statusName: (s: string) => string }) {
  const { t, user } = useSession();
  return (
    <Link to={`/projects/${project.id}`} className="card">
      {user?.isStaff && <p className="kicker">{project.workspaceName}</p>}
      <h3>{project.title}</h3>
      <p className="muted" style={{ margin: '0 0 8px', fontSize: 13 }}>
        {statusName(project.status)}
        {project.openTasks > 0 ? ` · ${t.openTasks(project.openTasks)}` : ''}
      </p>
      <Hours used={project.usedHours} total={project.totalHours} />
    </Link>
  );
}

function numberOr(raw: string | null): number | undefined {
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}
